import type { ReactNode } from 'react';
import { STATUS_TONE_CLASSES, type StatusTone } from '../tokens';

export type StatusPillSize = 'sm' | 'md';

export interface StatusPillProps {
  tone?: StatusTone;
  size?: StatusPillSize;
  /** Leading dot — the geometry RunStatusBadge/IntakeStatusBadge both used. */
  dot?: boolean;
  /** Icon-only square variant (incoming_requests/StatusPill.tsx). */
  iconOnly?: boolean;
  /** Leading icon (lucide element). */
  icon?: ReactNode;
  children?: ReactNode;
  className?: string;
  'data-testid'?: string;
}

const SIZES: Record<StatusPillSize, string> = {
  sm: 'px-2 py-0.5 text-ui-2xs',
  md: 'px-2.5 py-1 text-ui-xs',
};

/**
 * The ONE status pill. Absorbs RunStatusBadge, IntakeStatusBadge,
 * ExportsScreen.StatusPill, ResponseStatusPill and StatusPill (icon-only):
 * rounded-full geometry, one tone map (tokens/STATUS_TONE_CLASSES), two sizes.
 */
export function StatusPill({
  tone = 'neutral',
  size = 'sm',
  dot = false,
  iconOnly = false,
  icon,
  children,
  className = '',
  'data-testid': dataTestId,
}: StatusPillProps) {
  const toneClasses = STATUS_TONE_CLASSES[tone];
  if (iconOnly) {
    return (
      <span
        data-testid={dataTestId}
        className={`inline-flex items-center justify-center w-5 h-5 rounded-ui-sm border ${toneClasses} ${className}`}
        aria-hidden={children ? undefined : true}
      >
        {children ?? icon}
      </span>
    );
  }
  return (
    <span
      data-testid={dataTestId}
      className={`inline-flex items-center gap-1 rounded-full border font-medium leading-none ${SIZES[size]} ${toneClasses} ${className}`}
    >
      {icon}
      {dot && <span className="w-1.5 h-1.5 rounded-full bg-current shrink-0" />}
      {children}
    </span>
  );
}
